import { createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { size } from 'es-toolkit/compat';

import { OrchestrationResponse } from 'api/experimentResults';
import { getFeatureKeysByStudyAndOrchestration } from 'api/features';
import { FlowClassName } from 'interfaces/experimentResults';
import { NamedFeature } from 'interfaces/features';
import { useParsedBrackets } from 'utils/features/getFeaturesKeysObjectsWebWorker/useParsedBrackets';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import useMainFilters from './useMainFilters';
import useOrchestrationFilter from './useOrchestrationFilter';

export const featuresFilterOptions = createFilterOptions({
  stringify: (option: NamedFeature) => (option?.displayName || '') + ' ' + (option?.nameOverride || ''),
});

const useFeatureFilters = ({
  addStainToFeatureName: addStainToFeatureNameFromProps,
  studyId,
  orchestrationId,
  onSuccessGetExperimentDefinitions,
  includeInferredFeatures = false,
  includeHiddenFeatures = false,
  stainIndexFilter, // Filter results by stain index
  enabled = true,
}: {
  addStainToFeatureName?: boolean;
  studyId: string;
  orchestrationId: string;
  onSuccessGetExperimentDefinitions?: (data: OrchestrationResponse) => void;
  includeInferredFeatures?: boolean;
  includeHiddenFeatures?: boolean;
  stainIndexFilter?: string;
  enabled?: boolean;
}) => {
  const { isStudyIdSpecific } = useMainFilters();
  const studyIdIsSpecific = isStudyIdSpecific(studyId);
  const { queryParams } = useEncodedFilters();

  const { orchestrationsLoading, orchestrationIdOptions, orchestrations } = useOrchestrationFilter(
    studyId,
    queryParams.resultsMode,
    [FlowClassName.CalculateFeatures],
    onSuccessGetExperimentDefinitions
  );

  const canLoadFeatureKeys = studyIdIsSpecific && !orchestrationsLoading && enabled;
  const { data: featureKeysResponse, isLoading: featureKeysLoadingState } = useQuery({
    queryKey: [
      'featureKeys',
      {
        studyId,
        orchestrationId,
        includeInferredFeatures,
        includeHiddenFeatures,
        resultsMode: queryParams.resultsMode,
      },
    ],
    queryFn: () =>
      getFeatureKeysByStudyAndOrchestration(
        studyId,
        orchestrationId,
        includeInferredFeatures,
        includeHiddenFeatures,
        queryParams.resultsMode
      ),
    placeholderData: { featuresKeys: [] },
    enabled: canLoadFeatureKeys,
  });
  const featureKeysLoading = featureKeysLoadingState && canLoadFeatureKeys;

  const { data: stainTypesInStudy } = useStudyStainTypeIds(studyId, {
    enabled: enabled && isStudyIdSpecific && Boolean(studyId),
  });
  const addStainToFeatureName = addStainToFeatureNameFromProps || !isStudyIdSpecific || size(stainTypesInStudy) > 1;

  const {
    data: featuresData,
    isLoading: isParsingFeaturesDataState,
    canParseBrackets,
  } = useParsedBrackets({
    bracketsToParse: featureKeysResponse?.featuresKeys || [],
    orchestrationId,
    studyId,
    enabled,
    stainIndexFilter,
    addStainToFeatureName,
  });
  const isParsingFeaturesData = isParsingFeaturesDataState && canParseBrackets;

  return {
    orchestrationIdOptions,
    orchestrationsLoading,
    featuresLoading: featureKeysLoading || isParsingFeaturesData,
    orchestrations,
    featuresData: featuresData as NamedFeature[] | undefined,
  };
};

export default useFeatureFilters;
